import React, { useMemo } from 'react'
import { useDispatch } from 'react-redux';
import im from 'immer';
import { TrainingEvent } from '@alucio/aws-beacon-amplify/src/models'
import { DNAIconStyleProps } from '@alucio/lux-ui';
import useToast, { ToastActions } from '@alucio/lux-ui/src/components/Toast/useToast';
import { AppDispatch } from 'src/state/redux';
import { DNAModalActions } from 'src/state/redux/slice/DNAModal/DNAModal';
import { TrainingEventFormProvider } from './TrainingEventProvider';
import DNATrainingEventModal from 'src/components/DNA/Modal/DNATrainingEventModal';
import { trainingEventActions } from 'src/state/redux/slice/trainingEvent';
import DNACommonConfirmation from 'src/components/DNA/Modal/DNACommonConfirmation';

export enum TrainingEventContextActions {
  edit = 'Edit',
  delete = 'Delete'
}
type TrainingEventContextOptions = {
  [ket in keyof typeof TrainingEventContextActions]: {
    title: string,
    icon: string,
    iconStatus?: DNAIconStyleProps['status']
    onPress: (d: AppDispatch, f: TrainingEvent, toast: ToastActions) => () => void,
  }
}

type BindTrainingEventContextActions = {
  [key in keyof typeof TrainingEventContextActions]: (f: TrainingEvent) => () => void
}

export type TrainingEventActions = (keyof typeof TrainingEventContextActions)

export const useTrainingEventActions = (): BindTrainingEventContextActions => {
  const dispatch = useDispatch<AppDispatch>()
  const toast = useToast()

  const bound = useMemo(() => im(trainingEventContextActions, draft => {
    for (const action in trainingEventContextActions) {
      const actionTyped = action as keyof typeof TrainingEventContextActions
      draft[action] =
        (trainingEvent: TrainingEvent) => trainingEventContextActions[actionTyped]
          .onPress(dispatch, trainingEvent, toast)
    }
  }) as unknown as BindTrainingEventContextActions,
  [dispatch],
  );

  return bound
}

export const trainingEventContextActions: TrainingEventContextOptions = {
  edit: {
    title: TrainingEventContextActions.edit,
    icon: 'pencil-simple',
    onPress: (dispatch, trainingEvent) => () => {
      dispatch(DNAModalActions.setModal({
        isVisible: true,
        allowBackdropCancel: true,
        component: (props) => (
          <TrainingEventFormProvider
            tenantId={trainingEvent.tenantId}
            trainingEvent={trainingEvent}
            toggleModal={props.toggleModal}
          >
            <DNATrainingEventModal {...props} tenantId={trainingEvent.tenantId}/>
          </TrainingEventFormProvider>
        ),
      }))
    },
  },
  delete: {
    title: TrainingEventContextActions.delete,
    icon: 'trash',
    iconStatus: 'danger',
    onPress: (dispatch, trainingEvent) => () => {
      dispatch(DNAModalActions.setModal({
        isVisible: true,
        allowBackdropCancel: false,
        component: () => (
          <DNACommonConfirmation
            status="danger"
            cancelText="Cancel"
            confirmActionText="Delete"
            onConfirmAction={() => {
              dispatch(trainingEventActions.removeTrainingEvent(trainingEvent))
              dispatch(trainingEventActions.remove(trainingEvent))
            }}
            title="Delete event?"
            descriptionText="This action cannot be undone."
          />),
      }))
    },
  },
}
